import { useEffect, useRef, useState } from "react";

/**
 * Default polling interval (5 seconds).
 */
export const DEFAULT_POLL_INTERVAL_MS = 5_000;

/**
 * Default polling timeout (2 minutes) - after this we give up and surface a soft warning.
 */
export const DEFAULT_POLL_TIMEOUT_MS = 120_000;

/**
 * Polls a `useBackend` call until `isTerminal(call.data)` returns true or the timeout
 * elapses. Used by GoPay return pages (registration + marketplace) to reflect payment
 * settlement without manual refresh.
 *
 * Pure orchestration only - the consumer keeps full control over rendering and stop
 * conditions, so we avoid coupling registration- and marketplace-specific shapes here.
 *
 * @param {Object} backendCall - return value of useBackend (must expose `data` and `fetch`)
 * @param {(data: any) => boolean} isTerminal - returns true when polling should stop
 * @param {Object} [options]
 * @param {number} [options.intervalMs=5000]
 * @param {number} [options.timeoutMs=120000]
 * @param {boolean} [options.enabled=true] - allows callers to gate polling on a condition
 *  (e.g. `Boolean(id)`); when false, polling never starts
 * @returns {{ pollingActive: boolean, pollingTimeout: boolean }}
 */
function useBackendPolling(backendCall, isTerminal, options = {}) {
  const { intervalMs = DEFAULT_POLL_INTERVAL_MS, timeoutMs = DEFAULT_POLL_TIMEOUT_MS, enabled = true } = options;

  const [pollingActive, setPollingActive] = useState(true);
  const [pollingTimeout, setPollingTimeout] = useState(false);
  const pollStartRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled || !pollingActive || !backendCall.data) return;

    if (isTerminal(backendCall.data)) {
      setPollingActive(false);
      return;
    }

    const intervalId = setInterval(async () => {
      if (Date.now() - pollStartRef.current >= timeoutMs) {
        setPollingActive(false);
        setPollingTimeout(true);
        return;
      }
      await backendCall.fetch();
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [enabled, pollingActive, backendCall, isTerminal, intervalMs, timeoutMs]);

  return { pollingActive, pollingTimeout };
}

export default useBackendPolling;
